import { useEffect, useMemo, useState } from "react";
import { measureWidestText } from "src/utils/text-measure-utils";

type TSharedColumnWidthOptions = {
  /** Upper bound on the column, as a fraction of the container width, so the columns after it keep room. */
  maxFraction: number;
  /** Lower bound on the column — the column never gets narrower than this, whatever the container measures. */
  minWidth: number;
  /** Padding, margins and sub-pixel slack inside the cell that the text measurement does not cover. */
  extraWidth: number;
  /** Width the rest of the row needs and this column must not take: row furniture plus the widest row's own content. */
  reservedWidth: number;
};

/**
 * Resolves one column width shared by every row of a list: wide enough for the longest text, but
 * never wider than the space left once `reservedWidth` is set aside, and never below `minWidth`.
 *
 * Texts are measured off-DOM rather than from the rendered cells because the list is virtualized —
 * measuring mounted rows would make the column jump as rows recycle.
 *
 * Returns `undefined` until the container has been measured, so callers can fall back to a static width.
 *
 * Takes the container element rather than a ref: the lists that use this only render their root once
 * data has loaded, so a ref object would still be null on the single run of the observer effect.
 * Callers hold the element in state and pass `setState` as the callback ref.
 */
export const useSharedColumnWidth = (
  container: HTMLElement | null,
  texts: string[],
  font: string,
  { maxFraction, minWidth, extraWidth, reservedWidth }: TSharedColumnWidthOptions
): number | undefined => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [areFontsReady, setAreFontsReady] = useState(false);

  useEffect(() => {
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = entry.contentRect.width;
      // The options panel animates its width and the list sits in the flex sibling that gives way,
      // so an uncoalesced observer re-measures on every frame of that animation.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setContainerWidth(previous => (Math.abs(previous - nextWidth) < 1 ? previous : nextWidth)));
    });
    if (container) observer.observe(container);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [container]);

  useEffect(() => {
    let isMounted = true;
    // AvenirNextLTPro is loaded at runtime; measuring before it lands would use fallback metrics.
    // Without a Font Loading API there is nothing to wait for, so measure straight away rather than
    // leaving the column stuck on its fallback width forever.
    if (!document.fonts) {
      setAreFontsReady(true);
      return undefined;
    }

    document.fonts.ready.then(() => {
      if (isMounted) setAreFontsReady(true);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const contentWidth = useMemo(() => (areFontsReady ? measureWidestText(texts, font) + extraWidth : 0), [texts, font, extraWidth, areFontsReady]);

  return useMemo(() => {
    if (!containerWidth || !contentWidth) return undefined;
    // minWidth wins last: the column falls back to its historic fixed width rather than shrinking
    // below it when the rest of the row wants everything.
    return Math.max(minWidth, Math.min(contentWidth, containerWidth - reservedWidth, containerWidth * maxFraction));
  }, [containerWidth, contentWidth, minWidth, maxFraction, reservedWidth]);
};
